import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import PictureImage from '../Common/PictureImage';
import WhoAreWe from './WhoAreWe';
import WhyPPP from './WhyPPP';
import ServicesOverview from './ServicesOverview';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import Canonical from '../../seo/Canonical';
import AboutUsHero from './AboutUsHero';
import './AboutUs.css';

const AboutUs = () => {
  return (
    <section className="about-us logistics-page">
      <Helmet>
        <meta httpEquiv="X-Robots-Tag" content="index, follow" />
        <meta name="publisher" content="Pick Pack Pro" />
      </Helmet>
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      <AboutUsHero />

      <section className="about-story-section" aria-labelledby="about-story-heading">
        <div className="about-story-container">
          <h2 id="about-story-heading" className="about-story-title">
            Our Story: How Pick Pack Pro Became a Leading UK 3PL
          </h2>
          <p className="about-story-paragraph">
            Pick Pack Pro is a UK-based 3PL and Amazon FBA prep centre operating from Milton Keynes, supporting Amazon, Shopify, TikTok Shop and multi-channel sellers. We were founded with a clear mission: to help growing businesses overcome the fulfilment challenges
            that often limit long-term success. As a trusted UK fulfilment company, we recognised that many e-commerce
            brands had strong products and growing sales but lacked reliable e-commerce fulfilment, efficient warehousing,
            and scalable logistics. Delayed dispatches, inaccurate inventory and inconsistent order handling were affecting
            customer satisfaction and profitability. We built Pick Pack Pro to provide dependable{' '}
            <Link to="/blog/3pl-logistics-uk-explained-2026">3PL logistics UK</Link> services that allow businesses to grow
            with confidence.
          </p>
          <p className="about-story-paragraph">
            From our Milton Keynes fulfilment centre, we support Amazon sellers, Shopify merchants, TikTok Shop brands and
            multi-channel retailers with professional{' '}
            <Link to="/blog/pick-and-pack-fulfilment-uk-explained">pick and pack fulfilment</Link>, secure storage and{' '}
            <Link to="/blog/amazon-fba-prep-centre-uk-guide-2026">Amazon FBA prep</Link> services. Every stage of our
            fulfilment process is designed around accuracy, efficiency and transparency, ensuring your products move quickly
            from inbound delivery to final dispatch. By combining experienced warehouse teams with modern fulfilment
            technology, we help businesses streamline operations while maintaining excellent service standards.
          </p>
          <p className="about-story-paragraph">
            Our approach is built on three core values: accountability, transparency and continuous improvement. We take
            ownership of every order, communicate clearly with every client and continually refine our warehouse processes
            to improve speed, accuracy and operational performance. Whether preparing inventory for Amazon fulfilment
            centres, managing daily e-commerce orders or supporting promotional campaigns, our focus remains the same —
            delivering reliable fulfilment that protects your reputation.
          </p>
          <p className="about-story-paragraph">
            Today, Pick Pack Pro has become a trusted UK 3PL and e-commerce fulfilment partner for businesses that want more
            than simple storage. We provide flexible warehouse fulfilment,{' '}
            <Link to="/blog/shopify-fulfilment-company-uk">Shopify fulfilment</Link>,{' '}
            <Link to="/blog/tiktok-shop-fulfilment-uk">TikTok Shop fulfilment</Link>, inventory management and nationwide
            distribution from our strategically located Milton Keynes facility. Our goal is simple: deliver accurate,
            scalable and cost-effective fulfilment solutions that help your business grow while giving your customers the
            fast, reliable delivery experience they expect.
          </p>
        </div>
      </section>

      <section className="about-team-section" aria-labelledby="about-team-heading">
        <div className="about-team-container">
          <h2 id="about-team-heading" className="about-team-title">Leadership Team</h2>
          <div className="about-team-grid">
            <article className="about-team-card">
              <img
                src="/Images/Direct-Responsive-Communication.webp"
                alt="Operations Director at Pick Pack Pro leading UK fulfilment workflows"
                className="about-team-photo"
              />
              <h3 className="about-team-name">A. Khan</h3>
              <p className="about-team-role">Operations Director</p>
              <p className="about-team-bio">
                Leads day-to-day warehouse operations, SLA planning and process design across Amazon, Shopify and
                multi-channel fulfilment workflows.
              </p>
            </article>
            <article className="about-team-card">
              <img
                src="/Images/Live-Discord-Webhook-Notifications.webp"
                alt="Client Success Lead supporting e-commerce fulfilment clients in the UK"
                className="about-team-photo"
              />
              <h3 className="about-team-name">S. Malik</h3>
              <p className="about-team-role">Client Success Lead</p>
              <p className="about-team-bio">
                Owns onboarding, account communication and reporting cadence so clients get clear visibility from
                day one through scale-up.
              </p>
            </article>
            <article className="about-team-card">
              <img
                src="/Images/live-purchasing-sheet-sync.webp"
                alt="Systems Manager handling warehouse integrations and inventory sync"
                className="about-team-photo"
              />
              <h3 className="about-team-name">R. Ali</h3>
              <p className="about-team-role">Systems & Integrations Manager</p>
              <p className="about-team-bio">
                Manages platform integrations, inventory sync accuracy and channel routing logic to reduce manual work
                and prevent stock discrepancies.
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="about-credentials-section" aria-labelledby="about-credentials-heading">
        <div className="about-credentials-container">
          <h2 id="about-credentials-heading" className="about-credentials-title">
            Standards, Systems &amp; Credentials
          </h2>
          <p className="about-credentials-lead">
            At Pick Pack Pro, we combine proven warehouse processes, modern fulfilment technology and Amazon-compliant
            standards to deliver reliable UK fulfilment services. As a trusted Amazon FBA prep centre,{' '}
            <Link to="/blog/what-is-seller-fulfilled-prime-uk-complete-guide-2026">Seller Fulfilled Prime (SFP)</Link>{' '}
            partner and UK 3PL, our documented workflows, measurable SLAs and integrated warehouse management systems give
            e-commerce businesses complete confidence in every order.
          </p>
          <ul className="about-credentials-list">
            <li>
              <strong>Amazon FBA Prep Compliance</strong> — Professional FNSKU labelling, poly bagging, carton
              preparation, quality inspections and Amazon-compliant inbound shipments.
            </li>
            <li>
              <strong>Seller Fulfilled Prime Ready</strong> — SFP-ready operations with Buy Shipping integration, weekend
              dispatch capability and on-time shipment performance.
            </li>
            <li>
              <strong>Advanced Warehouse Management System</strong> — Mintsoft-powered WMS with 650+ marketplace and
              courier integrations, live inventory tracking and real-time stock synchronisation.
            </li>
            <li>
              <strong>Experienced UK Fulfilment Team</strong> — Over 5 years of expertise in pick and pack, warehouse
              fulfilment, inventory management, client success and operational excellence.
            </li>
          </ul>
        </div>
      </section>

      <section className="about-warehouse-section" aria-labelledby="about-warehouse-heading">
        <div className="about-warehouse-container">
          <h2 id="about-warehouse-heading" className="about-warehouse-title">
            Explore Our UK Fulfilment Warehouse
          </h2>
          <p className="about-warehouse-sub">
            Discover how our Milton Keynes fulfilment centre supports Amazon sellers, Shopify brands and growing
            e-commerce businesses with professional warehousing, inventory management and order fulfilment.
          </p>
          <div className="about-warehouse-grid">
            <PictureImage
              src="/Images/SFP.webp"
              alt="Pick Pack Pro warehouse aisle for Seller Fulfilled Prime and e-commerce fulfilment"
              className="about-warehouse-photo"
            />
            <img
              src="/Images/return-handling.webp"
              alt="Returns handling zone in Pick Pack Pro UK warehouse"
              className="about-warehouse-photo"
            />
            <img
              src="/Images/shopify-fulfilment-hero.webp"
              alt="Shopify and multi-channel order fulfilment operations at Pick Pack Pro"
              className="about-warehouse-photo"
            />
          </div>
        </div>
      </section>

      <WhoAreWe />
      <WhyPPP />
      <ServicesOverview />
      <Newsletter />
      <Footer />
    </section>
  );
};

export default AboutUs;
